'use strict';

import { cn_camera } from "../svg/cn_camera";
import { cn_add, cn_box, cn_clone, cn_dot, cn_mul, cn_normal, cn_normalize, cn_point_on_segment, cn_sub } from "../utils/cn_utilities";
import { cn_element } from "./cn_element";

//***********************************************************************************
//**** Abstract Vector element
//***********************************************************************************
export class cn_vector_element extends cn_element {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_vector_element';
        this.color = "";
        this.box = new cn_box();
    }

    /**
     * Unserialize method
     * @param {any} json 
     * @param {cn_vector_element} parent 
     * @returns {cn_vector_element}
     */
    static unserialize(json, parent) {
        if (json.class_name == 'cn_vector_text')
            return cn_vector_text.unserialize(json,parent);
        if (json.class_name == 'cn_vector_path')
            return cn_vector_path.unserialize(json,parent);
        if (json.class_name == 'cn_vector_arc')
            return cn_vector_arc.unserialize(json,parent);
        if (json.class_name == 'cn_vector_instance')
            return cn_vector_instance.unserialize(json,parent);
        return null;
    }

    apply_color(fields) {
        let html = "";
        let mode = this.get_root().draw_with_colors;
        let col = this.color;
        if (mode == 0) col = "black";
        if (col == "") {
            let layer = this.get_layer();
            if (!layer || layer.color == "")
                return "";
            col = layer.color;
        }
        if (col == "revert") 
            col = (mode==1)?"black":"white";
        fields.forEach(f => html += ` ${f}="${col}"`);
        return html;
    }

    get_root() {
        if (!this.parent)
            return this;
        return this.parent.get_root();
    }

    get_layer() {
        if (!this.parent)
            return this;
        return this.parent.get_root();
    }

    draw(camera, extra_classes, color, scale) {
        return "";
    }

    contains(point, precision, bounds) {
        return null;
    }

    get_bounding_box() {
        return new cn_box();
    }
    visit_vertices(func, bounds) {
    }
    visit_edges(func, bounds) {
    }

    get_vertices() {
        return [];
    }

    translate(v) {

    }
    
    in_bounding_box(pt,precision) {
        if (!this.box.posmin) return false;
        if (pt[0] < this.box.posmin[0] - precision || pt[0] > this.box.posmin[0] + this.box.size[0] + precision) return false;
        if (pt[1] < this.box.posmin[1] - precision || pt[1] > this.box.posmin[1] + this.box.size[1] + precision) return false;
        return true;
    }

    find_segment(pt, precision) {
        return null;
    }
};

export class cn_vector_text extends cn_vector_element {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_vector_text';
        this.position = [0,0];
        this.align = 0;
        this.text = '';
        this.rotation = 0;
        this.height = 0.1;
    }

    serialize() {
        const json = this.serialize_element();
        json.class_name = "cn_vector_text";
        json.position = [...this.position];
        json.align = this.align;
        json.text = this.text;
        json.rotation = this.rotation;
        json.height = this.height;
        return json;
    }

    static unserialize(json, parent) {
        const element = new cn_vector_text(parent);
        element.unserialize_element(json);
        element.position = [...json.position];
        element.align = json.align;
        element.text = json.text;
        element.rotation = json.rotation;
        element.height = json.height;
        return element;
    }

    /**
     * draw in SVG
     * @returns {string}
     */
    // @ts-ignore
    draw(ymax) {
        if (!this.get_root().show_text) return "";
        let alignments = ["start","middle","end"]
        let html = "";

        const lines = this.text.replace(/[{}]/g, '').split(/\\P/g);
    
        let tspanElements = lines.map((line, index) => {
            // Nettoyage final des codes de formatage résiduels
            const cleanLine = line.replace(/\\[a-zA-Z0-9|]+;?/g, '');
            
            // On utilise 'dy' pour l'interligne (1.2em est standard)
            return `<tspan x="${this.position[0]}" dy="${index === 0 ? 0 : '1.2em'}">${cleanLine}</tspan>`;
        }).join('');

        html += `<text 
            x="${this.position[0]}" 
            y="${(ymax - this.position[1])}" 
            transform="rotate(${-this.rotation})" 
            transform-origin="${this.position[0]} ${(ymax - this.position[1])}" 
            text-anchor="${alignments[this.align]}" 
            font-size="${this.height}"
            stroke="none"
            ${this.apply_color(["fill"])}
            >${tspanElements}</text>`;
        if (html.includes("NaN")) return "";
        return html;
    }

    get_bounding_box() {
        this.box = new cn_box();
        this.box.enlarge_point(this.position)
        return  this.box;
    }

    get_vertices() {return [this.position];}

    translate(v) {
        this.position = cn_add(this.position,v);
    }
}

//***********************************************************************************
//**** Vector Path element
//***********************************************************************************
export class cn_vector_path extends cn_vector_element {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_vector_path';
        // @type {Array<Array<number>>}
        this.vertices = [];
        this.closed_contour = false;
    }

    serialize() {
        const json = this.serialize_element();
        json.class_name = "cn_vector_path";
        json.closed_contour = this.closed_contour;
        json.vertices = this.vertices;
        return json;
    }

    static unserialize(json, parent) {
        const element = new cn_vector_path(parent);
        element.unserialize_element(json);
        if (typeof (json.closed_contour) == 'boolean')
            element.closed_contour = json.closed_contour;
        if (typeof (json.vertices) == 'object')
            element.vertices = json.vertices;
        return element;
    }

    /**
     * draw in SVG
     * @param {number} ymax
     * @returns {string}
     */
    // @ts-ignore
    draw(ymax) {
        let html = "";
        let vertices__parsed = 0
        html += `<path fill="none" stroke-width="1" ${this.apply_color(["stroke"])} d="M `;
        this.vertices.forEach((v, i) => {
            if (i == 1) html += "L ";
            html += `${v[0]} ${ymax-v[1]} `
            vertices__parsed++
        });
        if (this.closed_contour)
            html += "Z";
        html += `" />`;
        if (html.includes("NaN")) return "";
        return vertices__parsed ? html : '';
    }

    get_bounding_box() {
        this.box = new cn_box();
        this.vertices.forEach(v =>  this.box.enlarge_point(v));
        return  this.box;
    }

    /**
     * 
     * @param {*} point 
     * @param {*} precision 
     * @param {*} bounds 
     * @returns {{
     *      distance: number,
     *      position: number[],
     *      index: number,
     *      vertices: any[],
     *      element: any,
     *      layer: any
     * }}
     */
    contains(point, precision, bounds) {
        if (!this.vertices.some(v => v[0] > point[0] - precision)) return null;
        if (!this.vertices.some(v => v[1] > point[1] - precision)) return null;
        if (!this.vertices.some(v => v[0] < point[0] + precision)) return null;
        if (!this.vertices.some(v => v[1] < point[1] + precision)) return null;
        let nb_vertices = (this.closed_contour) ? this.vertices.length : this.vertices.length - 1;

        let res = null;
        for (var i = 0; i < nb_vertices; i++) {
            let v0 = this.vertices[i];
            let v1 = this.vertices[(i + 1) % this.vertices.length];
            if ([v0, v1].every(vertex => vertex[0] >= bounds[0][0] && vertex[0] <= bounds[1][0] && vertex[1] >= bounds[0][1] && vertex[1] <= bounds[1][1])) {
                const p = cn_sub(point, v0);
                const dir = cn_sub(v1, v0);
                const l = cn_normalize(dir);
                const x = cn_dot(p, dir);
                if (x < -precision || x > precision + l) continue;
                const y = Math.abs(cn_dot(p, cn_normal(dir)));
                if (y > precision) continue;
                let dist = y;
                if (x < 0 && -x > dist) dist = -x;
                if (x > l && x - l > dist) dist = x - l;
                res = {
                    distance: dist,
                    position: cn_add(v0, cn_mul(dir, x)),
                    index: i,
                    vertices: [v0, v1],
                    element: this,
                    layer: this.parent
                };
                precision = dist;
            }
        }
        return res;
    }

    find_segment(pt, precision) {
        if (!this.in_bounding_box(pt,precision)) return null;
        let nb_vertices = (this.closed_contour) ? this.vertices.length : this.vertices.length - 1;

        let res = null;
        for (var i = 0; i < nb_vertices; i++) {
            let v0 = this.vertices[i];
            let v1 = this.vertices[(i + 1) % this.vertices.length];
            if (cn_point_on_segment(pt,v0,v1,precision))
                return [v0,v1];
        }
        return null;
    }
    
    get_vertices() {return this.vertices;}

    visit_vertices(func) {
        this.vertices.forEach(v => func(v));
    }
    visit_edges(func) {
        let nb_vertices = (this.closed_contour) ? this.vertices.length : this.vertices.length - 1;
        for (var i = 0; i < nb_vertices; i++) {
            let v0 = this.vertices[i];
            let v1 = this.vertices[(i + 1) % this.vertices.length];
            func(v0, v1);
        }
    }
    
    translate(v) {
        this.vertices = this.vertices.map(vtx => cn_add(vtx,v));
    }
};

//***********************************************************************************
//**** Vector Path element
//***********************************************************************************
export class cn_vector_arc extends cn_vector_element {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_vector_arc';
        // @type {Array<Array<number>>}
        this.center = [0, 0];
        this.radius = [0, 0];
        this.angle = 0;
        this.start_angle = 0;
        this.end_angle = 0;
    }

    serialize() {
        const json = this.serialize_element();
        json.class_name = "cn_vector_arc";
        json.center = this.center;
        json.radius = this.radius;
        json.angle = this.angle;
        json.start_angle = this.start_angle;
        json.end_angle = this.end_angle;
        return json;
    }

    static unserialize(json, parent) {
        const element = new cn_vector_arc(parent);
        element.unserialize_element(json);
        if (typeof (json.center) == 'object')
            element.center = json.center;
        if (typeof (json.radius) == 'object')
            element.radius = json.radius;
        if (typeof (json.angle) == 'number')
            element.angle = json.angle;
        if (typeof (json.start_angle) == 'number')
            element.start_angle = json.start_angle;
        if (typeof (json.end_angle) == 'number')
            element.end_angle = json.end_angle;
        return element;
    }

    /**
     * draw in SVG
     * @param {number} ymax
     * @returns {string}
     */
    // @ts-ignore
    draw(ymax) {
        var html = "";
        html += `<path fill="none" ${this.apply_color(["stroke"])} d="`;
        const dx = [Math.cos(this.angle) * this.radius[0], Math.sin(this.angle) * this.radius[0]];
        const dy = [-Math.sin(this.angle) * this.radius[1], Math.cos(this.angle) * this.radius[1]];
        const v0 = cn_add(this.center, cn_add(cn_mul(dx, Math.cos(this.start_angle)), cn_mul(dy, Math.sin(this.start_angle))));
        const v1 = cn_add(this.center, cn_add(cn_mul(dx, Math.cos(this.end_angle)), cn_mul(dy, Math.sin(this.end_angle))));

        const radius0 = this.radius[0];
        const radius1 = this.radius[1];
        html += `M ${v0[0]} ${ymax-v0[1]} `;
        html += `A ${radius0} ${radius1} ${-this.angle} 0 0 ${v1[0]} ${ymax-v1[1]}`;
        html += `" />`;
        if (html.includes("NaN")) return "";
        return html;
    }

    get_bounding_box() {
        this.box = new cn_box();
        this.box.enlarge_point(cn_add(this.center, [this.radius, this.radius]));
        this.box.enlarge_point(cn_sub(this.center, [this.radius, this.radius]));
        return this.box;
    }

    
    get_vertices() {return [this.center];}

    translate(v) {
        this.center = cn_add(this.center,v);
    }
}

//***********************************************************************************
//**** Layer element
//***********************************************************************************
export class cn_vector_layer extends cn_vector_element {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_vector_layer';
        this.visible = true;
        this.label = "";
        this.color = "black";

        // @type {Array<cn_vector_element>}
        this.elements = [];
    }
    serialize() {
        const json = this.serialize_element();
        json.visible = this.visible;
        json.label = this.label;
        json.elements = this.elements.map(e => e.serialize());
        json.color = this.color;
        return json;
    }

    static unserialize(json, parent) {
        const layer = new cn_vector_layer(parent);
        layer.unserialize_element(json);
        layer.visible = json.visible;
        layer.label = json.label;
        layer.color = json.color || 'black';
        json.elements.forEach(e => {
            let ee = cn_vector_element.unserialize(e, layer);
            if (ee) layer.elements.push(ee);
        })
        return layer;
    }

    
    get_layer() {
        return this;
    }

    /**
     * draw in SVG
     * @param {number} ymax
     * @returns {string}
     */
    // @ts-ignore
    draw(ymax) {
        var html = "";
        html += `<g ${this.apply_color(["stroke","fill"])}>`;
        this.elements.forEach(element => html += element.draw(ymax));
        html += `</g>`;
        return html;
    }

    find_segment(position, precision) {
        let result = null;
        this.elements.find(element => {
            result = element.find_segment(position, precision);
            return result != null;
        });
        return result;
    }
    visit_vertices(func) {
        this.elements.forEach(element => element.visit_vertices(func));
    }
    visit_edges(func) {
        this.elements.forEach(element => element.visit_edges(func));
    }

    get_bounding_box() {
        this.box = new cn_box();
        this.elements.forEach(e => this.box.enlarge_box(e.get_bounding_box()));
        return this.box;
    }
    
    translate(v) {
        this.elements.forEach(e => e.translate(v));
    }
};

//***********************************************************************************
//**** Vector bloc element
//***********************************************************************************
export class cn_vector_bloc extends cn_vector_element {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_vector_bloc';
        this.name = "";
        this.elements = [];
        this.position = [0,0];
    }
    
    serialize() {
        const json = this.serialize_element();
        json.class_name = this.class_name;
        json.name = this.name;
        json.elements = this.elements.map(e => e.serialize());
        json.position = this.position;
        return json;
    }

    static unserialize(json, parent) {
        const bloc = new cn_vector_bloc(parent);
        bloc.unserialize_element(json);
        bloc.name = json.name;
        bloc.position = json.position;
        json.elements.forEach(e => {
            let ee = cn_vector_element.unserialize(e, bloc);
            if (ee) bloc.elements.push(ee);
        })
        return bloc;
    }

    get_bounding_box() {
        this.box = new cn_box();
        this.elements.forEach(e => this.box.enlarge_box(e.get_bounding_box()));
        return this.box;
    }
    
    /**
     * draw in SVG
     * @returns {string}
     */
    // @ts-ignore
    draw() {
        var html = "";
        //let box = this.get_bounding_box();
        html += `<symbol id="${this.name}" style="overflow: visible;" >`;

        //html += `<rect x="${box.posmin[0]}" y="${box.posmin[1]}" width="${box.size[0]}" height="${box.size[1]}" fill="green" opacity="0.1" />`;
        //html += `<circle cx="0" cy="0" r="${box.size[1]*0.01}" fill="green"  />`;

        html += `<g transform="translate(${-this.position[0]}, ${this.position[1]})">`;
        this.elements.forEach(element => html += element.draw(0));
        html += `</g></symbol>`;


        if (html.includes("NaN"))
        {
            return "";
        }
        this["html"] = html;
        return html;
    }
}

//***********************************************************************************
//**** Vector bloc element
//***********************************************************************************
export class cn_vector_instance extends cn_vector_element {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_vector_instance';
        this.name = "";
        this.position = [0,0];
        this.rotation = 0;
        this.scale = [1,1];
        this.bloc = null;
    }
    
    serialize() {
        const json = this.serialize_element();
        json.class_name = this.class_name;
        json.name = this.name;
        json.position = this.position;
        json.rotation = this.rotation;
        json.scale = this.scale;
        return json;
    }

    static unserialize(json, parent) {
        const instance = new cn_vector_instance(parent);
        instance.unserialize_element(json);
        instance.name = json.name;
        instance.position = json.position;
        instance.position = json.position;
        instance.rotation = json.rotation;
        instance.scale = json.scale;
        return instance;
    }

    get_bounding_box() {
        this.box = new cn_box();
        this.box.enlarge_point(this.position);
        return this.box;
    }
    
    /**
     * draw in SVG
     * @returns {string}
     */
    // @ts-ignore
    draw(ymax) {
        var html = "";
        let bloc = this.get_root().blocs.find(b => b.name == this.name);
        if (!bloc) return html;
        html += `<use href="#${this.name}" ${this.apply_color(["color","stroke","fill"])}`;
        html += ` transform="translate(${this.position[0]}, ${ymax-this.position[1]}) rotate(${-this.rotation}) scale(${this.scale[0]},${this.scale[1]})" />`;
        return html;
    }
    
    translate(v) {
        this.position = cn_add(this.position,v);
    }

    find_segment(pt, precision) {
        if (!this.in_bounding_box(pt,precision)) return null;
        return null;
    }
}


//***********************************************************************************
//**** Vector data
//***********************************************************************************
export class cn_vector_data extends cn_element {
    constructor(parent) {
        super(parent);
        this.class_name = 'cn_vector_data';
        // @type {Array<cn_vector_layer>}
        this.layers = [];
        this.show_text = true
        this.draw_with_colors = 1;
        this.blocs = [];

        this.box = new cn_box();
    }

    get_root() {
        return this;
    }

    get_bounding_box() {
        this.box = new cn_box();
        this.layers.forEach(layer => this.box.enlarge_box(layer.get_bounding_box()));
        return this.box;
    }
    serialize() {
        const json = this.serialize_element();
        json.layers = this.layers.map(l => l.serialize());
        json.show_text = this.show_text;
        json.draw_with_colors = this.draw_with_colors;
        json.blocs = this.blocs.map(b => b.serialize());
        return json;
    }

    static unserialize(json, parent) {
        const data = new cn_vector_data(parent);
        data.unserialize_element(json);

        if (json.blocs)
            data.blocs = json.blocs.map(jb => cn_vector_bloc.unserialize(jb,data));

        json.layers.forEach(l => {
            const layer = cn_vector_layer.unserialize(l, data);
            if (layer) data.layers.push(layer);
        })

        data.get_bounding_box();
        data.show_text = json.show_text ?? true;
        data.draw_with_colors = json.draw_with_colors ?? 1;

        return data;
    }

    filter_layers() {

        //*** We count the number of vertices */
        let nb_vertices = 0;
        this.layers.forEach(layer => {
            layer.elements.forEach(e => nb_vertices += e.get_vertices().length);
        });

        //*** We build a list of x and ys */
        let x_array = new Float64Array(nb_vertices);
        let y_array = new Float64Array(nb_vertices);
        let offset = 0;
        this.layers.forEach(layer => {
            layer.elements.forEach(e => {
                let vertices = e.get_vertices();
                for (var i=0;i<vertices.length;i++) {
                    x_array[offset+i] = vertices[i][0];
                    y_array[offset+i] = vertices[i][1];
                }
                offset += vertices.length;
         });
        });

        //*** we sort them */
        x_array.sort();
        y_array.sort();

        let search = 0.01;
        console.log(`x ${search} ${x_array[0]} - ${x_array[Math.floor(nb_vertices*search)]} - ${x_array[Math.floor(nb_vertices*(1-search))]} - ${x_array[nb_vertices-1]}`);
        console.log(`y ${search} ${y_array[0]} - ${y_array[Math.floor(nb_vertices*search)]} - ${y_array[Math.floor(nb_vertices*(1-search))]} - ${y_array[nb_vertices-1]}`);
        
        search = 0.1;
        console.log(`x ${search} ${x_array[0]} - ${x_array[Math.floor(nb_vertices*search)]} - ${x_array[Math.floor(nb_vertices*(1-search))]} - ${x_array[nb_vertices-1]}`);
        console.log(`y ${search} ${y_array[0]} - ${y_array[Math.floor(nb_vertices*search)]} - ${y_array[Math.floor(nb_vertices*(1-search))]} - ${y_array[nb_vertices-1]}`);
        
        //*** we choose to keep everything in the majority of elements */
        let posmin = [x_array[Math.floor(nb_vertices*search)],y_array[Math.floor(nb_vertices*search)]];
        let posmax = [x_array[Math.floor(nb_vertices*(1-search))],y_array[Math.floor(nb_vertices*(1-search))]];
        let size = [posmax[0]-posmin[0],posmax[1]-posmin[1]];
        posmin = cn_sub(posmin,size);
        posmax = cn_add(posmax,size);

        this.layers.forEach(layer => {
            layer.elements = layer.elements.filter(e => {
                let box = e.get_bounding_box();
                if (box.posmin[0] + box.size[0] < posmin[0]) return false;
                if (box.posmin[1] + box.size[1] < posmin[1]) return false;
                if (box.posmin[0] > posmax[0]) return false;
                if (box.posmin[1] > posmax[1]) return false;
                return true;
         });
        });
    }

    /**
     * draw in SVG
     */
    // @ts-ignore
    draw(scale) {
        var html = "";
        html += `<g transform="scale(${scale})">`;
        html += `<style>
                    *[stroke] { vector-effect: non-scaling-stroke; }
                    text {
                        font-family: "Arial", "Helvetica", sans-serif;
                        font-weight: 400;
                    }
                </style>`;

        if (this.draw_with_colors == 2) {
            html += `<rect fill="black" x="${this.box.posmin[0]}" y="${this.box.posmin[1]}" width="${this.box.size[0]}" height="${this.box.size[1]}" />`;
        }
        this.layers.filter(layer => layer.visible).forEach(layer => {
            html += layer.draw(this.box.size[1]);
            });
        html += `</g>`;
        return html;
    }

    find_segment(p0, precision) {
        let position = this.image_to_local(p0);
        let result = null;
        this.layers.filter(layer => layer.visible).find(layer => {
            result = layer.find_segment(position, precision);
            return result != null;
        });
        // @ts-ignore
        if (result) result = result.map(v => this.local_to_image(v));
        return result;
    }

    image_to_local(v) {
        return [this.box.posmin[0] + v[0] * this.box.size[0],this.box.posmin[1] + v[1] * this.box.size[1]];
    }

    local_to_image(v) {
        return [(v[0] - this.box.posmin[0])/(this.box.size[0]),(v[1] - this.box.posmin[1])/(this.box.size[1])];
    }

    visit_vertices(func) {
        this.layers.filter(layer => layer.visible).forEach(layer => layer.visit_vertices(v => func(this.local_to_image(v))));
    }
    visit_edges(func) {
        this.layers.filter(layer => layer.visible).forEach(layer => layer.visit_edges((v0, v1) => func(this.local_to_image(v0),this.local_to_image(v1))));
    }
    
    translate(v) {
        this.layers.forEach(l => l.translate(v));
    }
};
